import { apiClient } from '../../../shared/api';
import { toTeamTypeView } from '../lib/toTeamTypeView';
import type {
  CreateTeamRequest,
  CreateTeamResponse,
  MyTeamResponse,
  TeamOpsRosterResponse,
  TeamResponse,
  TeamWithMemberCount,
  UpdateTeamRequest,
} from '@aegislab/shared';

export type {
  CreateTeamRequest,
  UpdateTeamRequest,
  TeamOpsRosterResponse,
  TeamResponse,
  TeamWithMemberCount,
};

export const getTeams = async (): Promise<TeamWithMemberCount[]> => {
  const response = await apiClient.get<(TeamResponse & { memberCount?: number })[]>('/teams/list');
  // 동아리(CLUB)는 운영팀이 아니라 별도 surface(features/club, /clubs)에서 다룬다.
  // 여기서 제외하지 않으면 type 매핑이 CLUB 을 'task-force' 로 뭉개 운영팀 UI 에 TF 로 위장 노출된다.
  return response.data
    .filter((team) => team.type !== 'CLUB')
    .map((team) => ({
      ...team,
      displayName: team.name,
      memberCount: team.memberCount ?? 0,
      type: toTeamTypeView(team.type),
      status: team.status === 'ACTIVE' ? ('active' as const) : ('archived' as const),
    }));
};

export const getMyTeams = async (): Promise<TeamWithMemberCount[]> => {
  const response = await apiClient.get<MyTeamResponse[]>('/teams/my-teams');
  // 동아리(CLUB)는 운영팀이 아니라 별도 surface(features/club, /clubs)에서 다룬다.
  // 제외하지 않으면 팀운영 페이지(useRosterAdapter)에 CLUB 이 TF 로 위장 노출된다.
  return response.data
    .filter((team) => team.type !== 'CLUB')
    .map((team) => ({
      ...team,
      displayName: team.name,
      memberCount: team.memberCount ?? 0,
      type: toTeamTypeView(team.type),
      status: team.status === 'ACTIVE' ? ('active' as const) : ('archived' as const),
    }));
};

export const fetchTeamOpsRoster = async (): Promise<TeamOpsRosterResponse> => {
  const response = await apiClient.get<TeamOpsRosterResponse>('/teams/team-ops-roster');
  return response.data;
};

export const createTeam = async (data: CreateTeamRequest): Promise<TeamWithMemberCount> => {
  const endpoint = data.type === 'department' ? '/teams/department' : '/teams/task-force';
  const response = await apiClient.post<CreateTeamResponse>(endpoint, {
    name: data.name,
    leaderId: data.leaderId,
  });
  return {
    ...response.data.team,
    displayName: response.data.team.name,
    memberCount: 1,
    type: data.type,
    status: 'active' as const,
  };
};

export const updateTeam = async (
  teamId: string,
  data: UpdateTeamRequest
): Promise<TeamWithMemberCount> => {
  const response = await apiClient.patch<TeamResponse>(`/teams/${teamId}`, data);
  return {
    ...response.data,
    displayName: response.data.name,
    memberCount: 0,
    type: toTeamTypeView(response.data.type),
    status: response.data.status === 'ACTIVE' ? ('active' as const) : ('archived' as const),
  };
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  await apiClient.post(`/teams/${teamId}/archive`);
};

export const updateTeamCapabilities = async (
  teamId: string,
  capabilities: string[]
): Promise<TeamResponse> => {
  const response = await apiClient.patch<TeamResponse>(`/teams/${teamId}/capabilities`, {
    capabilities,
  });
  return response.data;
};
